import React, { useState } from "react";
import { Button } from "../design/button";
import { Divider } from "../design/divider";
import { ErrorMessage, FieldGroup, Fieldset, Label } from "../design/fieldset";
import { Heading } from "../design/heading";
import { Input } from "../design/input";
import { Field } from "../design/fieldset";
import { Link } from "../design/link";
import { Text } from "../design/text";
import LogoHorizontal from "../components/LogoHorizontal";
import { useTenant } from "../contexts/TenantContext";
import { PoweredByLogo } from "../components/PoweredByLogo";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

interface Errors {
  firstName?: string;
  lastName?: string;
  email?: string;
}

const RegisterPage: React.FC = () => {
  const { register } = useAuth();
  const { tenant, loading: loadingTenant } = useTenant();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSaving(true);
    try {
      await register(firstName, lastName, email, password);
      navigate("/login"); // Redirect to verify email page after registration
    } catch (error: any) {
      console.error("Registration failed:", error);
    }
    setIsSaving(false);
  };

  const validate = () => {
    const newErrors: Errors = {};
    if (!email) newErrors.email = "Email is required";
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      newErrors.email = "Invalid email address";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen">
      <form
        method="post"
        className="w-full max-w-lg p-6 bg-white rounded-lg shadow-lg dark:bg-gray-800"
        onSubmit={handleSubmit}
      >
        <div className="items-center w-full my-6 text-center">
          {!loadingTenant && (
            <LogoHorizontal
              href="/"
              tenant={tenant}
              className="items-center h-10 mx-auto"
            />
          )}
        </div>
        <Fieldset>
          <Heading className="text-2xl font-semibold text-center">
            Create Account
          </Heading>
          <Divider className="my-6" />
          <FieldGroup>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
              <Field>
                <Label>First Name</Label>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First Name"
                />
                {errors.firstName && (
                  <ErrorMessage>{errors.firstName}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Last Name</Label>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last Name"
                />
                {errors.lastName && (
                  <ErrorMessage>{errors.lastName}</ErrorMessage>
                )}
              </Field>
            </div>
          </FieldGroup>
          <FieldGroup>
            <Field>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                aria-label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
              />
              {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
            </Field>

            <Field>
              <Label>Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
              />
            </Field>
          </FieldGroup>
          <Divider className="my-6" />
          <Button type="submit" className="w-full" disabled={isSaving}>
            {isSaving ? "Creating account..." : "Create Account"}
          </Button>
          <div className="my-6 text-center">
            <Text>
              Already have an account?{" "}
              <Link href="/login" className="text-blue-600 hover:underline">
                Login here
              </Link>
            </Text>
          </div>
        </Fieldset>
      </form>
      <PoweredByLogo />
    </div>
  );
};

export default RegisterPage;
